<script setup lang="ts">
    import { timeFormatStandard } from '@shared/constants/date-time';
    import { dayjsFormatter, dayjsNow } from '@shared/helpers/date-time';
    import { type RangeSliderModeTyping, type RangeSliderNumberValueTyping, type RangeSliderTimeValueTyping, type RangeSliderValueTyping } from '@shared/typings/range-slider';
    import NumberInput from './number-input.vue';
    import TimeInput from './time-input.vue';

    const props = defineProps<{
        modelValue: RangeSliderValueTyping
        min: number | string
        max: number | string
        step?: number
        mode?: RangeSliderModeTyping
        label?: string
        startLabel?: string
        endLabel?: string
        disabled?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<(event: 'update:modelValue', value: RangeSliderValueTyping) => void>();

    // Const
    const PERCENT_MULTIPLIER = 100;
    const MINUTES_PER_HOUR = 60;
    const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
    const DEFAULT_STEP = 1;
    const DEFAULT_STEP_TIME = 10;

    // Helpers
    const timeToMinutes = (time: string): number => {
        const parsed = dayjsFormatter(time, timeFormatStandard);
        return (parsed.hour() * MINUTES_PER_HOUR) + parsed.minute();
    };

    const minutesToTime = (totalMinutes: number): string =>
        dayjsNow()
            .startOf('day')
            .add(totalMinutes, 'minute')
            .format(timeFormatStandard);

    const parseBoundary = (val: number | string): number => {
        if (typeof val === 'string') return timeToMinutes(val);
        return val;
    };

    const formatDisplayValue = (value: number): string => {
        if ((props.mode ?? 'number') === 'time') return minutesToTime(value);
        return String(value);
    };

    const isTimeValue = (val: RangeSliderValueTyping): val is RangeSliderTimeValueTyping =>
        typeof val.min === 'string';

    // When a time range crosses midnight (e.g. 22:00–02:00), the max expressed
    // in raw minutes will be smaller than the min. We offset by 24h so the
    // native range input always receives a valid min < max.
    const toInternalValue = (value: RangeSliderValueTyping): RangeSliderNumberValueTyping => {
        if (isTimeValue(value)) {
            const minMin = timeToMinutes(value.min);
            const maxMin = timeToMinutes(value.max);
            return {
                min: minMin,
                max: maxMin <= minMin ? maxMin + MINUTES_PER_DAY : maxMin,
            };
        }
        return {
            min: value.min,
            max: value.max,
        };
    };

    const toEmitValue = (minVal: number, maxVal: number): RangeSliderValueTyping => {
        if (props.mode === 'time') {
            return {
                min: minutesToTime(minVal),
                max: minutesToTime(maxVal),
            };
        }
        return {
            min: minVal,
            max: maxVal,
        };
    };

    const toPercent = (
        value: number,
        boundaryMin: number,
        boundaryMax: number,
    ): number =>
        ((value - boundaryMin) / (boundaryMax - boundaryMin)) * PERCENT_MULTIPLIER;

    // Refs
    const initialValues: RangeSliderNumberValueTyping = toInternalValue(props.modelValue);
    const minHandle: Ref<number> = ref(initialValues.min);
    const maxHandle: Ref<number> = ref(initialValues.max);

    // Computed
    const currentStep: ComputedRef<number> = computed(() => {
        if (typeof props.step !== 'undefined') return props.step;
        return (props.mode ?? 'number') === 'time' ? DEFAULT_STEP_TIME : DEFAULT_STEP;
    });
    const internalMin: ComputedRef<number> = computed(() => parseBoundary(props.min));
    const internalMax: ComputedRef<number> = computed(() => {
        const raw = parseBoundary(props.max);
        return raw <= internalMin.value ? raw + MINUTES_PER_DAY : raw;
    });
    const minPercent: ComputedRef<number> = computed(() => toPercent(
        minHandle.value,
        internalMin.value,
        internalMax.value,
    ));
    const maxPercent: ComputedRef<number> = computed(() => toPercent(
        maxHandle.value,
        internalMin.value,
        internalMax.value,
    ));
    const trackFillStyle = computed(() => ({
        insetInlineStart: `${minPercent.value}%`,
        insetInlineEnd: `${PERCENT_MULTIPLIER - maxPercent.value}%`,
    }));
    const minLabel: ComputedRef<string> = computed(() => formatDisplayValue(minHandle.value));
    const maxLabel: ComputedRef<string> = computed(() => formatDisplayValue(maxHandle.value));
    const isMinOnTop: ComputedRef<boolean> = computed(() =>
        minHandle.value >= internalMax.value - currentStep.value);

    // Methods
    const commitMin = (parsed: number): void => {
        if (Number.isNaN(parsed)) return;
        const clamped = Math.min(Math.max(parsed, internalMin.value), maxHandle.value - currentStep.value);
        minHandle.value = clamped;
        emit('update:modelValue', toEmitValue(clamped, maxHandle.value));
    };

    const commitMax = (parsed: number): void => {
        if (Number.isNaN(parsed)) return;
        const clamped = Math.min(Math.max(parsed, minHandle.value + currentStep.value), internalMax.value);
        maxHandle.value = clamped;
        emit('update:modelValue', toEmitValue(minHandle.value, clamped));
    };

    const minStringModel: WritableComputedRef<string> = computed({
        get: (): string => minutesToTime(minHandle.value),
        set: (val: string): void => { commitMin(timeToMinutes(val)) },
    });
    const maxStringModel: WritableComputedRef<string> = computed({
        get: (): string => minutesToTime(maxHandle.value),
        set: (val: string): void => {
            const raw = timeToMinutes(val);
            const adjusted = raw <= internalMin.value ? raw + MINUTES_PER_DAY : raw;
            commitMax(adjusted);
        },
    });
    const minNumberModel: WritableComputedRef<number> = computed({
        get: (): number => minHandle.value,
        set: (val: number): void => { commitMin(val) },
    });
    const maxNumberModel: WritableComputedRef<number> = computed({
        get: (): number => maxHandle.value,
        set: (val: number): void => { commitMax(val) },
    });

    const handleMinInput = (event: Event): void => {
        const input = event.target as HTMLInputElement;
        const parsed = Number(input.value);
        const newMin = Math.min(parsed, maxHandle.value - currentStep.value);
        minHandle.value = newMin;
        emit('update:modelValue', toEmitValue(newMin, maxHandle.value));
    };

    const handleMaxInput = (event: Event): void => {
        const input = event.target as HTMLInputElement;
        const parsed = Number(input.value);
        const newMax = Math.max(parsed, minHandle.value + currentStep.value);
        maxHandle.value = newMax;
        emit('update:modelValue', toEmitValue(minHandle.value, newMax));
    };

    // Watchers
    watch(
        () => props.modelValue,
        newValue => {
            const updated = toInternalValue(newValue);
            minHandle.value = updated.min;
            maxHandle.value = updated.max;
        },
        { deep: true },
    );
</script>

<template>
    <div
        :class="{ 'range-slider--disabled': disabled }"
        class="range-slider"
        data-constell="constell-range-slider"
    >
        <label
            v-if="label"
            class="range-slider__label"
        >
            {{ label }}
        </label>
        <div class="range-slider__inputs">
            <div
                v-if="mode === 'time'"
                class="range-slider__inputs-number"
            >
                <TimeInput
                    v-model="minStringModel"
                    :label="startLabel"
                    name="minDateInput"
                    ignore
                    data-constell="constell-range-slider-min-date-input"
                />
                <TimeInput
                    v-model="maxStringModel"
                    :label="endLabel"
                    name="maxDateInput"
                    ignore
                    data-constell="constell-range-slider-max-date-input"
                />
            </div>
            <div
                v-if="mode === 'number'"
                class="range-slider__inputs-time"
            >
                <NumberInput
                    v-model="minNumberModel"
                    :label="startLabel"
                    name="minNumberInput"
                    ignore
                    secondary
                    data-constell="constell-range-slider-min-number-input"
                />
                <NumberInput
                    v-model="maxNumberModel"
                    :label="endLabel"
                    name="maxNumberInput"
                    ignore
                    secondary
                    data-constell="constell-range-slider-max-number-input"
                />
            </div>
        </div>
        <div class="range-slider__range">
            <span class="range-slider__value range-slider__value--min">
                {{ minLabel }}
            </span>
            <div class="range-slider__track-container">
                <div class="range-slider__track">
                    <div
                        class="range-slider__fill"
                        :style="trackFillStyle"
                    />
                </div>
                <input
                    class="range-slider__input range-slider__input--min"
                    :class="{ 'range-slider__input--on-top': isMinOnTop }"
                    :min="internalMin"
                    :max="internalMax"
                    :step="currentStep"
                    :value="minHandle"
                    :disabled="disabled"
                    :aria-label="minLabel"
                    type="range"
                    data-constell="constell-range-slider-min-input"
                    @input="handleMinInput"
                >
                <input
                    class="range-slider__input range-slider__input--max"
                    :class="{ 'range-slider__input--on-top': !isMinOnTop }"
                    :min="internalMin"
                    :max="internalMax"
                    :step="currentStep"
                    :value="maxHandle"
                    :disabled="disabled"
                    :aria-label="maxLabel"
                    type="range"
                    data-constell="constell-range-slider-max-input"
                    @input="handleMaxInput"
                >
            </div>
            <span class="range-slider__value range-slider__value--max">
                {{ maxLabel }}
            </span>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .range-slider {
        display: flex;
        flex-direction: column;

        &--disabled {
            cursor: not-allowed;
            opacity: 0.6;
            pointer-events: none;
        }

        &__label {
            display: inline-block;
            padding: 0;
            color: var(--text-secondary);
            font-size: var(--font-regular);
            font-weight: var(--font-weight-light);
            margin-block-end: var(--spacing-01);
        }

        &__range {
            display: flex;
            align-items: center;
            padding: var(--spacing-03);
            border: 1px solid var(--border-tertiary);
            border-radius: var(--border-radius-rounded);
            background-color: var(--background-form-input);
            gap: var(--spacing-03);
            margin-block-start: var(--spacing-03);
        }

        &__track-container {
            position: relative;
            flex: 1;
            block-size: var(--size-2);
        }

        &__track {
            position: absolute;
            border-radius: var(--border-radius-xs);
            background-color: var(--color-white);
            block-size: 8px;
            inset-block-start: calc(50% - 4px);
            inset-inline: 0 0;
        }

        &__fill {
            position: absolute;
            border-radius: var(--border-radius-xs);
            background-color: var(--background-primary-invert);
            block-size: 100%;
        }

        &__inputs-number,
        &__inputs-time {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--spacing-03);

            > * {
                flex: 1;
            }
        }

        &__input {
            position: absolute;
            z-index: 0;
            margin: 0;
            appearance: none;
            background: transparent;
            block-size: 100%;
            inline-size: 100%;
            inset-block-start: 0;
            pointer-events: none;

            &--on-top {
                z-index: 1;
            }

            &::-webkit-slider-runnable-track {
                background: transparent;
                block-size: 100%;
            }

            &::-webkit-slider-thumb {
                position: relative;
                border: 2px solid var(--border-primary);
                border-radius: var(--border-radius-circle);
                appearance: none;
                background-color: var(--color-white);
                block-size: var(--size-2);
                cursor: pointer;
                inline-size: var(--size-2);
                pointer-events: all;
            }

            &:focus-visible::-webkit-slider-thumb {
                box-shadow: 0 0 0 3px var(--background-overlay-invert);
            }

            &::-moz-range-track {
                background: transparent;
            }

            &::-moz-range-thumb {
                border: 2px solid var(--border-primary);
                border-radius: var(--border-radius-circle);
                background-color: var(--color-white);
                block-size: var(--size-2);
                box-shadow: none;
                cursor: pointer;
                inline-size: var(--size-2);
                pointer-events: all;
            }

            &:focus-visible::-moz-range-thumb {
                box-shadow: 0 0 0 3px var(--background-overlay-invert);
            }

            &:disabled::-webkit-slider-thumb {
                border-color: var(--border-disabled-1);
                background-color: var(--background-disabled-2);
                cursor: not-allowed;
            }

            &:disabled::-moz-range-thumb {
                border-color: var(--border-disabled-1);
                background-color: var(--background-disabled-2);
                cursor: not-allowed;
            }
        }

        &__value {
            color: var(--text-primary);
            font-size: var(--font-regular);
            letter-spacing: 1px;
            line-height: 1;
            min-inline-size: 48px;
            white-space: nowrap;

            &--min {
                text-align: end;
            }

            &--max {
                text-align: start;
            }
        }
    }
</style>
